import React, { useEffect } from 'react';
import I18n from 'i18n-js';
import gql from 'graphql-tag';
import { withGraphql } from '@reverbdotcom/commons/src/with_graphql';
import { ChildProps } from 'react-apollo';
import CheckoutLoadingIndicator from './checkout_loading_indicator';
import {
  CoreCheckoutFetchDigitalWalletPaymentCheckout,
  core_apimessages_CheckoutAction_Type as CheckoutActionType,
  core_apimessages_Checkout_Status as CheckoutStatus,
} from '../schemas/rql';
import Location from '../lib/wrapped_location';
import { withMobileNativeProtocol } from './shared/url_helpers';

type Checkout = CoreCheckoutFetchDigitalWalletPaymentCheckout.Checkout;
export type CheckoutItemPageReturnError = 'PAYMENT_FAILURE' | 'CHECKOUT_FAILURE' | 'STANDARD_USER_ATTEMPTED_GUEST_CHECKOUT_FAILURE';
export type CheckoutItemPageReturnErrorCallback = (errorType: CheckoutItemPageReturnError, message: string) => any;

interface IProps {
  checkoutId?: string;
  onError: CheckoutItemPageReturnErrorCallback;
}

type Props = ChildProps<IProps, CoreCheckoutFetchDigitalWalletPaymentCheckout.Query>;

export function CheckoutItemPageReturnLayout(props: Props) {
  const { data } = props;
  const checkout = data?.checkout;
  const isLoading = data?.loading;
  
  function onError(errorType: CheckoutItemPageReturnError, message: string) {
    data.stopPolling();
    props.onError(errorType, message);
  }

  useItemPagePaymentAction({
    checkout,
    isLoading,
    onError,
  });
  
  return (
    <div className= "reverb-modal__container">
      <div className="checkout-loading-indicator__wrapper">
        <CheckoutLoadingIndicator
          title={I18n.t('discovery.checkout.redirectReturnLayout.paymentProcessing')}
        />
      </div>
    </div>
  );
}

function useItemPagePaymentAction({
  checkout,
  isLoading,
  onError,
}: {
  checkout: Checkout;
  isLoading: boolean;
  onError: CheckoutItemPageReturnErrorCallback;
}) {

  useEffect(() => {
    if (!isLoading && checkout) {
      if (checkout.action.type === CheckoutActionType.CHECKOUT_SUCCESS) {
        Location.assign(checkout.action.redirectUrl);
      }

      const errorType = findCheckoutErrorType(checkout);
    
      if (errorType) {   
        onError(errorType, checkout.paymentErrorMessage);
      }
    }
  }, [checkout, isLoading, onError]);
}

function findCheckoutErrorType(checkout: Checkout): CheckoutItemPageReturnError {
  if (checkout.status === CheckoutStatus.STANDARD_USER_ATTEMPTED_GUEST_CHECKOUT_FAILURE) {
    return checkout.status;
  }
  if (checkout.action.type === CheckoutActionType.PAYMENT_FAILURE || checkout.action.type === CheckoutActionType.CHECKOUT_FAILURE) {
    return checkout.action.type;
  }
  return undefined;
}

const withQuery = withGraphql<IProps, CoreCheckoutFetchDigitalWalletPaymentCheckout.Query>(
  gql`
    query Core_Checkout_FetchDigitalWalletPaymentCheckout($id: String, $reverbApp: String){
      checkout(input: { id: $id }) {
        _id
        action(input: { reverbApp: $reverbApp}) {
          type
          redirectUrl
        }
        status
        paymentErrorMessage
        orderBundle {
          _id
          id
        }
      }
    }
  `,
  {
    options: (ownProps) => {
      return {
        variables: {
          id: ownProps.checkoutId,
          reverbApp: withMobileNativeProtocol(),
        },
        pollInterval: 500,
        errorPolicy: 'ignore',
        fetchPolicy: 'network-only',
      };
    },
  },
);

export default withQuery(CheckoutItemPageReturnLayout);
